import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import { apDungTuyChinh, docTuyChinh, luuTuyChinh } from '../styles/tuy-chinh';
import type { TuyChinh } from '../styles/tuy-chinh';

/**
 * Hai chủ đề chọn được.
 *
 * Chủ đề `vibrant` đã **bỏ** (ADR-UI-0007): bảng màu của nó bị đánh giá là xấu,
 * và nhu cầu "đổi màu cho khác đi" giờ do bộ tùy chỉnh phục vụ đúng hơn.
 */
export type Theme = 'light' | 'dark';

/** Khóa lưu chủ đề trong localStorage. Phải khớp script chặn FOUC ở index.html. */
export const THEME_STORAGE_KEY = 'foxai-theme';

const THEME_CLASSES: readonly Theme[] = ['dark'];

/** `auto` theo hệ điều hành, là mặc định (không có khoá trong `localStorage`). */
export type CheDoNen = 'auto' | Theme;

interface ThemeContextValue {
  theme: Theme;
  cheDo: CheDoNen;
  setCheDo: (cheDo: CheDoNen) => void;
  tuyChinh: TuyChinh;
  setTuyChinh: (tc: TuyChinh) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

/** Đọc chủ đề đang nằm trên <html>, do script chặn FOUC đặt trước khi vẽ. */
function readThemeFromDocument(): Theme {
  return document.documentElement.classList.contains('dark') ? 'dark' : 'light';
}

const heToiHienTai = (): boolean => window.matchMedia('(prefers-color-scheme: dark)').matches;

function docCheDo(): CheDoNen {
  try {
    const luu = localStorage.getItem(THEME_STORAGE_KEY);
    return luu === 'light' || luu === 'dark' ? luu : 'auto';
  } catch {
    return 'auto';
  }
}

function ghiClassChuDe(next: Theme): void {
  const root = document.documentElement;
  root.classList.remove(...THEME_CLASSES);
  if (next !== 'light') root.classList.add(next);
}

function nhoCheDo(cheDo: CheDoNen): void {
  try {
    if (cheDo === 'auto') localStorage.removeItem(THEME_STORAGE_KEY);
    else localStorage.setItem(THEME_STORAGE_KEY, cheDo);
  } catch {
    // Chế độ riêng tư hoặc bị chặn lưu: vẫn đổi được chủ đề, chỉ không nhớ.
  }
}

const chuDeCua = (cheDo: CheDoNen): Theme => (cheDo === 'auto' ? (heToiHienTai() ? 'dark' : 'light') : cheDo);

/**
 * Chủ đề là một **class trên `<html>`**: không có class (sáng) hoặc `.dark`.
 * Không dùng thuộc tính `[data-theme]` (§5.14).
 *
 * Provider đọc lại lựa chọn đã lưu lúc mount; script chặn FOUC đã đặt class
 * đúng từ trước khi vẽ nên không có nhấp nháy.
 *
 * Tùy chỉnh của người dùng (màu nhấn, bo góc) được ghi lên `<html>` dưới dạng
 * biến CSS inline — xem `styles/tuy-chinh.ts`.
 */
export function ThemeProvider({ children }: { children: ReactNode }) {
  const [cheDo, setCheDoState] = useState<CheDoNen>(docCheDo);
  const [theme, setThemeState] = useState<Theme>(readThemeFromDocument);
  const [tuyChinh, setTuyChinhState] = useState<TuyChinh>(docTuyChinh);

  /*
   * Áp lại tùy chỉnh mỗi khi nó đổi **hoặc** khi đổi chủ đề: thang primary của
   * nền sáng và nền tối ngược chiều nhau, nên đổi chủ đề mà không áp lại sẽ để
   * nguyên thang của chủ đề cũ.
   */
  useEffect(() => {
    apDungTuyChinh(tuyChinh, theme === 'dark');
  }, [tuyChinh, theme]);

  const setCheDo = useCallback((next: CheDoNen) => {
    const chuDe = chuDeCua(next);
    nhoCheDo(next);
    ghiClassChuDe(chuDe);
    setCheDoState(next);
    setThemeState(chuDe);
  }, []);

  const setTuyChinh = useCallback((tc: TuyChinh) => {
    luuTuyChinh(tc);
    setTuyChinhState(tc);
  }, []);

  // Chế độ tự động: đi theo hệ điều hành khi nó đổi sáng/tối.
  useEffect(() => {
    if (cheDo !== 'auto') return;
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = (event: MediaQueryListEvent) => {
      const chuDe: Theme = event.matches ? 'dark' : 'light';
      ghiClassChuDe(chuDe);
      setThemeState(chuDe);
    };
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, [cheDo]);

  const value = useMemo(
    () => ({ theme, cheDo, setCheDo, tuyChinh, setTuyChinh }),
    [theme, cheDo, setCheDo, tuyChinh, setTuyChinh],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme phải được gọi bên trong <ThemeProvider>');
  return ctx;
}
